import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * icon: either a string key for CustomIcon (rendered via <CustomIcon name={key} />)
 * or a MUI component reference (for icons without custom SVGs).
 *
 * Each rule:
 *  - id: unique identifier
 *  - condition(state, derived): boolean — whether this insight applies
 *  - priority: 1 (highest) to 5 (lowest)
 *  - icon: MUI icon component OR string name for CustomIcon
 *  - titleKey/bodyKey/ctaLabelKey: i18n keys
 *  - ctaPath: path for CTA link (relative to /:lng/)
 *  - ctaParams: query params for the comparator link (deep linking)
 *  - annualImpact(state, derived): optional function returning annual $ impact string
 *  - emotional: if true, body uses urgent framing
 */
export const INSIGHT_RULES = [
  {
    id: 'high-housing',
    condition: (_, derived) => derived.housingRatio > 35,
    priority: 1,
    icon: 'house',
    titleKey: 'budgetPlanner.insights.highHousing.title',
    titleDefault: 'Tu vivienda consume mucho de tus ingresos',
    bodyKey: 'budgetPlanner.insights.highHousing.body',
    bodyDefault: 'Estás gastando más del 35% de tus ingresos en vivienda. Explorar opciones de hipoteca podría reducir tus costos mensuales.',
    ctaLabelKey: 'budgetPlanner.insights.highHousing.cta',
    ctaLabelDefault: 'Comparar hipotecas',
    ctaPath: 'hipotecas#programas-newcomer',
    ctaParams: { type: 'mortgage', intent: 'reduce-housing', maxRate: '5.5' },
    annualImpact: (_, derived) => {
      const excess = derived.monthlyHousing - (derived.monthlyIncome * 0.30);
      return excess > 0 ? Math.round(excess * 12) : null;
    },
  },
  {
    id: 'no-emergency-fund',
    condition: (_, derived) => derived.currentEmergencyMonths < 1,
    priority: 1,
    icon: 'piggyBank',
    emotional: true,
    titleKey: 'budgetPlanner.insights.noEmergency.title',
    titleDefault: 'Sin fondo de emergencia',
    bodyKey: 'budgetPlanner.insights.noEmergency.body',
    bodyDefault: 'Si pierdes tu empleo mañana, tienes {{months}} meses cubiertos. Una cuenta de ahorro con alta tasa de interés te ayuda a construir tu colchón financiero.',
    ctaLabelKey: 'budgetPlanner.insights.noEmergency.cta',
    ctaLabelDefault: 'Abrir cuenta de ahorro HISA',
    ctaPath: 'comparar-cuentas',
    ctaParams: { type: 'savings', intent: 'emergency-fund', feature: 'high-interest' },
  },
  {
    id: 'credit-card-debt',
    condition: (state) => state.debts.some((d) => d.rate >= 15),
    priority: 1,
    icon: 'creditCard',
    titleKey: 'budgetPlanner.insights.ccDebt.title',
    titleDefault: 'Deuda de tarjeta con interés alto',
    bodyKey: 'budgetPlanner.insights.ccDebt.body',
    bodyDefault: 'Tienes deuda con tasa de interés alta (>15%). Una tarjeta de transferencia de saldo podría ahorrarte cientos de dólares en intereses.',
    ctaLabelKey: 'budgetPlanner.insights.ccDebt.cta',
    ctaLabelDefault: 'Ver tarjetas balance transfer',
    ctaPath: 'tarjetas-de-credito',
    ctaParams: { type: 'balance-transfer', feature: '0-intro-apr' },
    annualImpact: (state) => {
      const ccDebts = state.debts.filter((d) => d.rate >= 15);
      const annualInterest = ccDebts.reduce((sum, d) => sum + (d.balance * d.rate / 100), 0);
      return annualInterest > 0 ? Math.round(annualInterest) : null;
    },
  },
  {
    id: 'high-groceries',
    condition: (_, derived) => derived.monthlyGroceries > 600,
    priority: 3,
    icon: ShoppingCartIcon,
    titleKey: 'budgetPlanner.insights.highGroceries.title',
    titleDefault: 'Gasto alto en supermercado',
    bodyKey: 'budgetPlanner.insights.highGroceries.body',
    bodyDefault: 'Gastas más de $600/mes en alimentos. Una tarjeta de cashback en supermercado podría darte hasta 5% de vuelta.',
    ctaLabelKey: 'budgetPlanner.insights.highGroceries.cta',
    ctaLabelDefault: 'Ver tarjetas cashback',
    ctaPath: 'tarjetas-de-credito',
    ctaParams: { type: 'cashback', intent: 'groceries', feature: 'grocery-bonus' },
    annualImpact: (_, derived) => Math.round(derived.monthlyGroceries * 12 * 0.03),
  },
  {
    id: 'good-savings',
    condition: (_, derived) => derived.monthlyNet > 500,
    priority: 4,
    icon: 'investments',
    titleKey: 'budgetPlanner.insights.goodSavings.title',
    titleDefault: 'Buen margen de ahorro',
    bodyKey: 'budgetPlanner.insights.goodSavings.body',
    bodyDefault: 'Estás ahorrando más de $500/mes. Considera invertir en GICs o TFSA para hacer crecer tu dinero con protección de capital.',
    ctaLabelKey: 'budgetPlanner.insights.goodSavings.cta',
    ctaLabelDefault: 'Ver tasas GIC',
    ctaPath: 'tasas-gic',
    ctaParams: { type: 'gic', intent: 'savings', term: '1-year' },
    secondaryLabelKey: 'budgetPlanner.insights.goodSavings.secondary',
    secondaryLabelDefault: 'Aprende sobre TFSA',
    secondaryPath: 'cuentas-registradas',
    annualImpact: (_, derived) => Math.round(derived.monthlyNet * 12 * 0.04),
  },
  {
    id: 'no-insurance',
    condition: (state) => {
      const insuranceCats = ['Seguros', 'Insurance', 'Assurances'];
      return !state.fixedExpenses.some((e) =>
        insuranceCats.some((cat) => (e.category || '').toLowerCase().includes(cat.toLowerCase()))
      );
    },
    priority: 5,
    icon: 'shield',
    titleKey: 'budgetPlanner.insights.noInsurance.title',
    titleDefault: 'Sin seguro detectado',
    bodyKey: 'budgetPlanner.insights.noInsurance.body',
    bodyDefault: 'No encontramos gastos en seguros en tu presupuesto. Como recién llegado, un seguro de vida o dental puede proteger a tu familia.',
    ctaLabelKey: 'budgetPlanner.insights.noInsurance.cta',
    ctaLabelDefault: 'Comparar seguros',
    ctaPath: 'comparar-seguros',
    ctaParams: { type: 'insurance', coverage: 'life,dental' },
  },
  {
    id: 'high-debt-ratio',
    condition: (_, derived) => derived.debtToIncomeRatio > 30,
    priority: 2,
    icon: WarningIcon,
    emotional: true,
    titleKey: 'budgetPlanner.insights.highDebt.title',
    titleDefault: 'Ratio deuda/ingreso alto',
    bodyKey: 'budgetPlanner.insights.highDebt.body',
    bodyDefault: 'Tu relación deuda/ingreso supera el 30%. Esto puede afectar tu capacidad para obtener crédito. Considera un plan de pago agresivo.',
    ctaLabelKey: 'budgetPlanner.insights.highDebt.cta',
    ctaLabelDefault: 'Ver opciones de consolidación',
    ctaPath: 'banca',
    ctaParams: { type: 'consolidation', intent: 'debt-reduction' },
  },
  {
    id: 'sending-remittances',
    condition: (state) => {
      const remittanceCats = ['Remesas', 'Remittances', 'Transferts'];
      return state.variableExpenses.some((e) =>
        remittanceCats.some((cat) => (e.name || '').toLowerCase().includes(cat.toLowerCase()) || (e.category || '').toLowerCase().includes(cat.toLowerCase()))
      );
    },
    priority: 3,
    icon: 'globe',
    titleKey: 'budgetPlanner.insights.remittances.title',
    titleDefault: 'Envías dinero al exterior',
    bodyKey: 'budgetPlanner.insights.remittances.body',
    bodyDefault: 'Detectamos gastos de remesas. Compara servicios para encontrar las mejores tasas y menores comisiones.',
    ctaLabelKey: 'budgetPlanner.insights.remittances.cta',
    ctaLabelDefault: 'Comparar servicios de remesas',
    ctaPath: 'comparar-remesas',
    ctaParams: { type: 'remittance', corridor: 'latam' },
  },
];
